import { forwardRef, PropsWithChildren, useId, useMemo } from 'react';
import composeRefs from '@seznam/compose-react-refs';
import { useIsomorphicLayoutEffect } from 'motion/react';
import { RadioGroup as RadioGroupPrimitive } from 'radix-ui';

import { useFocusRing } from '@components/primitives/FocusRing';
import { cn } from '@components/primitives/utilities';

import { RadioItemContext, useRadioGroupContext } from './context';

import css from './styles.module.css';

export interface RadioItemProps extends PropsWithChildren {
  id?: string;
  value: string;
  className?: string;
  disabled?: boolean;
}

/**
 * One selectable option. Its parts — `RadioGroup.Indicator`,
 * `RadioGroup.Icon`, `RadioGroup.Label`, `RadioGroup.Description` — render in
 * the order they are written as direct children of the item. The item is a
 * grid, so the stylesheet places each part by its own class and the item never
 * re-parents what it is given.
 */
export const RadioGroupItem = forwardRef<HTMLButtonElement, RadioItemProps>(
  function RadioGroupItem(
    { children, value, className, id: propsId, disabled },
    ref
  ) {
    const { registerItem } = useRadioGroupContext();

    const internalId = useId();
    const id = propsId ?? internalId;

    const [itemRef, focusStyle] = useFocusRing<HTMLButtonElement>();

    // Layout effect so the group's column count settles before the first paint.
    useIsomorphicLayoutEffect(() => registerItem(id), [registerItem, id]);

    const context = useMemo(() => ({ id }), [id]);

    return (
      <RadioItemContext.Provider value={context}>
        <RadioGroupPrimitive.Item
          ref={composeRefs(ref, itemRef)}
          id={id}
          value={value}
          disabled={disabled}
          className={cn(css.item, className)}
          style={focusStyle}
        >
          {children}
        </RadioGroupPrimitive.Item>
      </RadioItemContext.Provider>
    );
  }
);
